import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const Home: React.FC = () => {
  const { t } = useTranslation();

  const scrollToSection = (section: string) => {
    const element = document.getElementById(section);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 64, behavior: 'smooth' });
    }
  };

  // Scroll reveal
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      className="min-h-screen flex items-center relative overflow-hidden"
      style={{ background: 'var(--gradient-bg-hero), var(--color-bg)' }}
    >
      {/* Decorative orbs */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-10%', left: '50%', transform: 'translateX(-50%)',
          width: '900px', height: '600px',
          background: 'radial-gradient(ellipse, rgba(54,109,198,0.12) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '10%', right: '5%',
          width: '500px', height: '400px',
          background: 'radial-gradient(ellipse, rgba(124,58,237,0.10) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-28 relative z-10 w-full">
        <div className="flex flex-col md:flex-row items-center gap-16">
          {/* Left content */}
          <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left reveal">
            {/* Badge */}
            <span className="badge badge-primary mb-6">
              IoT · Intégration · Mobile · Web
            </span>

            <h1
              className="mb-6 font-display"
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                lineHeight: 1.15,
                color: 'var(--color-text-primary)',
              }}
            >
              <span className="text-gradient">
                {t('home.hero.title', "Innovative IT Solutions for Tomorrow's Challenges")}
              </span>
            </h1>

            <p
              className="mb-10 max-w-xl"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('home.hero.subtitle', 'Transforming ideas into powerful digital solutions with expertise in IoT, APIs, and application development.')}
            </p>

            <div className="flex gap-3 w-full">
              <button
                onClick={() => scrollToSection('services')}
                className="btn-ghost text-base py-3 flex-1"
              >
                {t('home.hero.cta.primary', 'Discover Our Services')}
              </button>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openContactChat'))}
                className="btn-primary text-base py-3 flex-1"
              >
                {t('home.hero.cta.secondary', 'Contact Us')}
              </button>
            </div>
          </div>

          {/* Right — image with glass glow */}
          <div className="w-full md:w-1/2 relative reveal" style={{ '--delay': '120ms' } as React.CSSProperties}>
            <div
              className="absolute -inset-8 rounded-full"
              style={{
                background: 'radial-gradient(ellipse, rgba(54,109,198,0.18) 0%, transparent 70%)',
                filter: 'blur(30px)',
              }}
            />
            <div
              className="relative rounded-2xl overflow-hidden p-4"
              style={{ borderRadius: 'var(--radius-2xl)', background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.03)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
            >
              <img
                src="/full_logo.svg"
                alt="Blue Owl SAS"
                className="w-full h-auto relative z-10"
                style={{ padding: '8%' }}
              />
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-20 grid grid-cols-3 gap-3 reveal" style={{ '--delay': '200ms' } as React.CSSProperties}>
          {[
            { value: '10+', label: 'Projets livrés' },
            { value: '5+', label: 'Clients satisfaits' },
            { value: '4', label: 'Domaines' },
          ].map(({ value, label }) => (
            <div key={label} className="card-glass text-center" style={{ padding: 'clamp(12px, 3vw, 24px) clamp(8px, 2vw, 16px)' }}>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(1.25rem, 4.5vw, 2rem)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  lineHeight: 1.1,
                }}
              >
                {value}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'clamp(0.65rem, 2vw, 0.875rem)',
                  color: 'var(--color-text-secondary)',
                  marginTop: 4,
                  lineHeight: 1.3,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
